import { useState } from "react"
import { Link, usePage } from "@inertiajs/react"

import "../../../css/homepage-hero.css"
import SellBackground from "../../../../public/images/static/gc-banner-rifle-pistol.webp"
import GoogleReviewsMark from "../../../../public/images/static/gc-google-reviews-badge.svg"
import GunBrokerMark from "../../../../public/images/static/gc-gunbroker-top-seller.webp"
import BbbMark from "../../../../public/images/static/hp-bbb-accredited-reversed.png"
import FflMark from "../../../../public/images/static/hp-ffl-mark-alt.svg"
import { HeroListingCarousel } from "./HeroListingCarousel"
import { HeroRotatingWord } from "./HeroRotatingWord"
import { HeroTrustStrip } from "./HeroTrustStrip"

const ATF_FFL_URL =
    "https://fflezcheck.atf.gov/FFLEzCheck/fflSearch?licsRegn=1&licsDis=59&licsSeq=52473"

/**
 * The homepage's first screen (TECH-1138): the auction hero, the "We also buy
 * …" sell pitch and the trust strip, merged from what were three blocks.
 *
 * The auction half comes first in the DOM at every width. "Sell a Gun to Us"
 * stays the <h2> because it carries the keyword, and "We also buy …" stays a
 * <p>: that split is deliberate.
 *
 * Analytics, per MM-337: GTM's existing trigger reads `data-cta` and
 * `data-outbound`, and these values are shared with the seller silo pages, so
 * reports pair them with the page path. Nothing else in the hero may carry
 * either attribute.
 */
export const HomepageHero = () => {
    const { most_watchers: listings, hero_sold: soldListings, year } =
        usePage().props
    const [isPaused, setIsPaused] = useState(false)

    return (
        <section
            className="falh"
            aria-labelledby="hero-heading"
            data-paused={isPaused ? "true" : "false"}
        >
            <div className="falh-bleed">
                <div className="container mx-auto px-5 xl:px-10">
                    <div className="falh-split">
                        <div className="falh-auction">
                            <div className="falh-intro">
                                <h1 id="hero-heading" className="falh-h1">
                                    {`Online Gun Auction ${year}`}
                                </h1>
                                <p className="falh-sub">
                                    Bid on top-quality firearms and accessories,
                                    verified for authenticity and performance.{" "}
                                    <b>Join thousands of satisfied collectors.</b>
                                </p>
                                <Link
                                    href="/auctions"
                                    className="falh-cta"
                                    data-cta="hero"
                                >
                                    View Live Auctions
                                </Link>
                            </div>

                            <HeroListingCarousel
                                listings={listings ?? []}
                                soldListings={soldListings ?? []}
                            />
                        </div>

                        <section
                            className="falh-sell"
                            aria-labelledby="hero-sell-heading"
                        >
                            <img
                                className="falh-sell-bg"
                                src={SellBackground}
                                alt=""
                                width="2625"
                                height="1750"
                                loading="lazy"
                                decoding="async"
                            />
                            <div className="falh-sell-veil" aria-hidden="true" />

                            <p className="falh-also">
                                <span className="falh-also-lead">We also buy</span>{" "}
                                <HeroRotatingWord isPaused={isPaused} />
                            </p>
                            <h2 id="hero-sell-heading" className="falh-h2">
                                Sell a Gun to Us
                            </h2>
                            <p className="falh-para">
                                FirearmLand is a licensed gun buyer. Three easy
                                steps: get a cash offer, often within a few
                                hours, ship free, get paid without leaving home.
                            </p>

                            <div className="falh-actions">
                                <Link
                                    href="/sell-my-gun"
                                    className="falh-pill falh-pill-primary"
                                    data-cta="cash-fork"
                                >
                                    Sell a gun to us
                                </Link>
                                <p className="falh-sublabel">
                                    We buy it outright. Guaranteed offer.
                                </p>
                                <Link
                                    href="/gun-consignment"
                                    className="falh-pill falh-pill-ghost"
                                    data-cta="consign-fork"
                                >
                                    Consign a gun
                                </Link>
                                <p className="falh-sublabel">
                                    We auction it for you. About 14 days.
                                </p>
                            </div>

                            <div className="falh-marks-wrap">
                                <div className="falh-marks">
                                    <img
                                        src={GoogleReviewsMark}
                                        alt="Google Reviews"
                                        width="147"
                                        height="63"
                                    />
                                    <img
                                        src={GunBrokerMark}
                                        alt="GunBroker Top 10 Seller"
                                        width="116"
                                        height="77"
                                    />
                                    <img
                                        className="falh-bbb"
                                        src={BbbMark}
                                        alt="BBB Accredited Business"
                                        width="160"
                                        height="63"
                                    />
                                    <a
                                        className="falh-ffl"
                                        href={ATF_FFL_URL}
                                        target="_blank"
                                        rel="noopener nofollow"
                                        aria-label="Verify our federal firearms license with the ATF"
                                        data-outbound="atf-hero"
                                    >
                                        <img
                                            src={FflMark}
                                            alt=""
                                            width="177"
                                            height="64"
                                        />
                                    </a>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            <HeroTrustStrip
                isPaused={isPaused}
                onTogglePause={() => setIsPaused((paused) => !paused)}
            />
        </section>
    )
}
